import type { Department } from "../../contexts/AuthProvider";
import { isInPTO } from "../../utils/dateUtils";
import { type Nurse, NurseFullRoleShort, type NurseInShift, NurseRole, type Shift } from "../../utils/models";
import { isSameShiftWindow, type ShiftWindow } from "../../utils/scheduleConflict";
import { countDailyHours } from "../../utils/statsCalculations";
import { getShiftWorkHours } from "../../utils/utilFunctions";
import { isAgencyNurse } from "../../utils/warningDialogs/utils";
import { buildEmployeeStatusWarning, type EmployeeStatusWarningProps } from "../../utils/warningDialogs/WarningDialog";

export type QuickAddPersonWarningInputs = {
	/**
	 * `checkWarnings`' agency flag. In this cascade it only SKIPS the time-off / daily-hours / overtime checks
	 * (agency hours are not tracked against those limits) — it never opens an agency confirmation, in add or move.
	 */
	isAgency: boolean;
	/** `undefined` when the hours are not worth asking about (an untouched move adds none, or agency staff). */
	dailyHours: number | undefined;
	hasPto: boolean;
	employeeStatusWarning: EmployeeStatusWarningProps | undefined;
};

type Params = {
	nurse: Nurse;
	department: Department;
	weeklyShifts: Shift[];
	dayIndex: number;
	window: ShiftWindow;
	movingShift: NurseInShift | undefined;
	jobManagementEnabled: boolean;
};

const dailyHoursWith = ({ nurse, department, weeklyShifts, dayIndex, window, movingShift, jobManagementEnabled }: Params): number =>
	countDailyHours({
		nurseId: nurse.id,
		departmentId: department.department_id,
		weeklyShifts,
		dayIndex,
		breakRulesConfig: department.breakRulesConfig ?? [],
		// The moved shift is replaced, not joined, so its old hours must not be summed alongside the new ones.
		excludedShiftId: movingShift?.id,
		nurseUnion: nurse.union,
	}) +
	getShiftWorkHours(
		department.department_id,
		window.endTime,
		window.startTime,
		department.breakRulesConfig ?? [],
		NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole],
		nurse.employmentType,
		nurse.jobIds,
		jobManagementEnabled,
		nurse.union,
	);

/**
 * The person-specific inputs to the "+" dialog's warning cascade (everything but the staffing target, which is
 * about the destination and always asked). Add mode is unchanged. A move whose times are untouched adds no
 * hours, so daily hours and time off are only re-checked when the times changed. Someone already on the day's
 * schedule was already let onto it, so a move raises no employee-status warning — and no agency one either,
 * though an agency person's hours checks are still skipped exactly as on an add.
 */
export const quickAddPersonWarningInputs = (params: Params): QuickAddPersonWarningInputs => {
	const { nurse, department, window, movingShift } = params;
	const isAgency = isAgencyNurse(nurse.employmentType);
	const checkHours = !isAgency && (!movingShift || !isSameShiftWindow(movingShift, window));
	// DEFAULT: an unchecked input is `undefined` / `false`, which `checkWarnings` reads as "no warning" — exactly
	// the outcome for a check that does not apply (agency staff's hours, an untouched move's hours and time off, a
	// move's employee status), not a stand-in for a value that failed to load.
	return {
		isAgency,
		dailyHours: checkHours ? dailyHoursWith(params) : undefined,
		hasPto: checkHours && nurse.employeeSpecificPreferences.some((pref) => isInPTO(pref, window.startTime, department.dayStart, window.endTime)),
		employeeStatusWarning: movingShift ? undefined : buildEmployeeStatusWarning(nurse, department.department_id, window.startTime),
	};
};
